import { useState, useMemo } from 'react';
import {
  Download,
  Gift,
  Loader2,
  Printer,
  Search,
  CreditCard,
  TrendingUp,
  DollarSign,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useCurrency } from '@/hooks/useCurrency';
import { useReportPrint } from '@/hooks/useReportPrint';
import { format } from 'date-fns';
import { DateRangeSelect, type DateRangeValue } from '@/components/ui/date-range-select';
import { datePresets, presetValue } from '@/lib/datePresets';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from '@/components/ui/chart';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';

type StatusFilter = 'all' | 'active' | 'used' | 'expired' | 'cancelled';
const DATE_RANGE_PRESETS = datePresets('all_time', 'week', 'month', 'last_month', 'year');

const CHART_COLORS = [
  'hsl(142, 76%, 36%)',  // green - active
  'hsl(217, 91%, 60%)',  // blue - used
  'hsl(25, 95%, 53%)',   // orange - expired
  'hsl(0, 84%, 60%)',    // red - cancelled
];

export default function GiftCardReports() {
  const { organization } = useAuth();
  const { formatCurrency } = useCurrency();
  const { printRef, handlePrint } = useReportPrint({ title: 'Gift card report' });

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [dateSel, setDateSel] = useState<DateRangeValue>(() => presetValue('all_time'));
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate date filters ('all_time' means no date filter)
  const dateFilters = useMemo(
    () =>
      dateSel.preset === 'all_time'
        ? null
        : { start: dateSel.start.toISOString(), end: dateSel.end.toISOString() },
    [dateSel]
  );

  // Fetch gift cards
  const { data: giftCards, isLoading } = useQuery({
    queryKey: ['gift-card-report', organization?.id, statusFilter, dateFilters],
    queryFn: async () => {
      if (!organization?.id) return [];

      let query = supabase
        .from('gift_cards')
        .select(`
          id,
          code,
          initial_balance,
          current_balance,
          status,
          expires_at,
          created_at,
          updated_at,
          customer:customers(id, first_name, last_name, email),
          purchased_by:customers!gift_cards_purchased_by_customer_id_fkey(id, first_name, last_name)
        `)
        .eq('organization_id', organization.id)
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      if (dateFilters) {
        query = query.gte('created_at', dateFilters.start).lte('created_at', dateFilters.end);
      }

      const { data, error } = await query.limit(500);

      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Fetch gift card transactions
  const { data: transactions } = useQuery({
    queryKey: ['gift-card-transactions', organization?.id, dateFilters],
    queryFn: async () => {
      if (!organization?.id) return [];

      // Get all gift card IDs for this org
      const { data: cards } = await supabase
        .from('gift_cards')
        .select('id')
        .eq('organization_id', organization.id);

      if (!cards?.length) return [];

      let query = supabase
        .from('gift_card_transactions')
        .select(`
          id,
          gift_card_id,
          type,
          amount,
          balance_after,
          created_at,
          gift_card:gift_cards(code)
        `)
        .in('gift_card_id', cards.map(c => c.id))
        .order('created_at', { ascending: false });

      if (dateFilters) {
        query = query.gte('created_at', dateFilters.start).lte('created_at', dateFilters.end);
      }

      const { data, error } = await query.limit(200);

      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id,
  });

  // Filter gift cards by search
  const filteredGiftCards = useMemo(() => {
    if (!giftCards) return [];
    if (!searchQuery) return giftCards;

    const query = searchQuery.toLowerCase();
    return giftCards.filter(card => {
      const customer = card.customer as any;
      return (
        card.code.toLowerCase().includes(query) ||
        customer?.email?.toLowerCase().includes(query) ||
        `${customer?.first_name || ''} ${customer?.last_name || ''}`.toLowerCase().includes(query)
      );
    });
  }, [giftCards, searchQuery]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!giftCards?.length) return null;

    const totalIssued = giftCards.reduce((sum, c) => sum + (c.initial_balance || 0), 0);
    const totalRedeemed = giftCards.reduce((sum, c) => sum + ((c.initial_balance || 0) - (c.current_balance || 0)), 0);
    const outstandingBalance = giftCards.reduce((sum, c) => sum + (c.current_balance || 0), 0);
    
    const byStatus = {
      active: giftCards.filter(c => c.status === 'active').length,
      used: giftCards.filter(c => c.status === 'used').length,
      expired: giftCards.filter(c => c.status === 'expired').length,
      cancelled: giftCards.filter(c => c.status === 'cancelled').length,
    };

    return {
      totalCards: giftCards.length,
      totalIssued,
      totalRedeemed,
      outstandingBalance,
      redemptionRate: totalIssued > 0 ? (totalRedeemed / totalIssued) * 100 : 0,
      byStatus,
    };
  }, [giftCards]);

  // Chart data - status distribution
  const statusChartData = useMemo(() => {
    if (!summaryStats?.byStatus) return [];
    return [
      { name: 'Active', value: summaryStats.byStatus.active, fill: CHART_COLORS[0] },
      { name: 'Used', value: summaryStats.byStatus.used, fill: CHART_COLORS[1] },
      { name: 'Expired', value: summaryStats.byStatus.expired, fill: CHART_COLORS[2] },
      { name: 'Cancelled', value: summaryStats.byStatus.cancelled, fill: CHART_COLORS[3] },
    ].filter(d => d.value > 0);
  }, [summaryStats]);

  // Chart data - transaction activity by day
  const transactionChartData = useMemo(() => {
    if (!transactions?.length) return [];

    const dailyMap = new Map<string, { loaded: number; redeemed: number; refunded: number }>();
    
    transactions.forEach(txn => {
      const date = format(new Date(txn.created_at), 'MMM d');
      if (!dailyMap.has(date)) {
        dailyMap.set(date, { loaded: 0, redeemed: 0, refunded: 0 });
      }
      const day = dailyMap.get(date)!;
      if (txn.type === 'loaded') day.loaded += txn.amount;
      if (txn.type === 'redeemed') day.redeemed += Math.abs(txn.amount);
      if (txn.type === 'refunded') day.refunded += Math.abs(txn.amount);
    });

    return Array.from(dailyMap.entries())
      .map(([date, data]) => ({ date, ...data }))
      .reverse()
      .slice(-14);
  }, [transactions]);

  const chartConfig: ChartConfig = {
    loaded: { label: 'Loaded', color: CHART_COLORS[0] },
    redeemed: { label: 'Redeemed', color: CHART_COLORS[1] },
    refunded: { label: 'Refunded', color: CHART_COLORS[2] },
  };

  // Export CSV
  const exportToCSV = () => {
    if (!filteredGiftCards.length) return;

    const headers = ['Code', 'Status', 'Initial Balance', 'Current Balance', 'Redeemed', 'Customer', 'Email', 'Created', 'Expires'];
    const rows = filteredGiftCards.map(card => {
      const customer = card.customer as any;
      return [
        card.code,
        card.status,
        card.initial_balance,
        card.current_balance,
        (card.initial_balance || 0) - (card.current_balance || 0),
        customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : '-',
        customer?.email || '-',
        format(new Date(card.created_at), 'yyyy-MM-dd'),
        card.expires_at ? format(new Date(card.expires_at), 'yyyy-MM-dd') : 'Never',
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gift-card-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>;
      case 'used':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Fully Used</Badge>;
      case 'expired':
        return <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">Expired</Badge>;
      case 'cancelled':
        return <Badge variant="destructive">Cancelled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-semibold">Gift Card Reports</h1>
          <p className="text-sm text-muted-foreground">
            Track gift card sales, redemptions, and outstanding balances
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={handlePrint}>
            <Printer className="h-4 w-4" />
            Print
          </Button>
          <Button variant="outline" size="sm" className="gap-2" onClick={exportToCSV}>
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Gift Cards</span>
              <Gift className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : summaryStats?.totalCards || 0}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {summaryStats?.byStatus?.active || 0} active
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Issued</span>
              <CreditCard className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalIssued || 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Redeemed</span>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {isLoading ? '...' : formatCurrency(summaryStats?.totalRedeemed || 0)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {(summaryStats?.redemptionRate || 0).toFixed(1)}% redemption rate
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Outstanding Balance</span>
              <DollarSign className="h-4 w-4 text-amber-500" />
            </div>
            <p className="text-2xl font-bold mt-2 text-amber-600">
              {isLoading ? '...' : formatCurrency(summaryStats?.outstandingBalance || 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by code, customer, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[140px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="used">Fully Used</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <DateRangeSelect presets={DATE_RANGE_PRESETS} value={dateSel} onChange={setDateSel} />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution */}
            <Card>
              <CardHeader>
                <CardTitle>Status Distribution</CardTitle>
                <CardDescription>Gift cards by current status</CardDescription>
              </CardHeader>
              <CardContent>
                {statusChartData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[250px]">
                    <PieChart>
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            valueFormatter={(value) => `${value} cards`}
                          />
                        }
                      />
                      <Pie
                        data={statusChartData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        nameKey="name"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        labelLine={false}
                      >
                        {statusChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No gift card data
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transaction Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Transaction Activity</CardTitle>
                <CardDescription>Gift card loads and redemptions</CardDescription>
              </CardHeader>
              <CardContent>
                {transactionChartData.length > 0 ? (
                  <ChartContainer config={chartConfig} className="h-[250px]">
                    <BarChart data={transactionChartData}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="date"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value) => `$${value}`}
                      />
                      <ChartTooltip
                        content={
                          <ChartTooltipContent
                            valueFormatter={(value) => formatCurrency(value)}
                          />
                        }
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                      <Bar dataKey="loaded" fill={CHART_COLORS[0]} stackId="a" radius={[0, 0, 0, 0]} />
                      <Bar dataKey="redeemed" fill={CHART_COLORS[1]} stackId="b" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ChartContainer>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                    No transaction data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Gift Cards Table */}
          <Card>
            <CardHeader>
              <CardTitle>Gift Cards</CardTitle>
              <CardDescription>
                {filteredGiftCards.length} gift cards
                {statusFilter !== 'all' && ` (${statusFilter})`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50 hover:bg-muted/50">
                    <TableHead>Code</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead className="text-right">Initial</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead>Usage</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredGiftCards.slice(0, 100).map((card) => {
                    const customer = card.customer as any;
                    const usagePercent = card.initial_balance > 0 
                      ? ((card.initial_balance - card.current_balance) / card.initial_balance) * 100
                      : 0;

                    return (
                      <TableRow key={card.id}>
                        <TableCell className="font-mono font-medium">
                          {card.code}
                        </TableCell>
                        <TableCell>{getStatusBadge(card.status || 'active')}</TableCell>
                        <TableCell>
                          {customer ? (
                            <div>
                              <span className="font-medium">
                                {`${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown'}
                              </span>
                              {customer.email && (
                                <p className="text-xs text-muted-foreground">{customer.email}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(card.initial_balance)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums font-medium">
                          {formatCurrency(card.current_balance)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Progress value={usagePercent} className="h-2 w-16" />
                            <span className="text-xs text-muted-foreground w-10">
                              {usagePercent.toFixed(0)}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(card.created_at), 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {card.expires_at 
                            ? format(new Date(card.expires_at), 'MMM d, yyyy')
                            : 'Never'
                          }
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredGiftCards.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No gift cards found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {filteredGiftCards.length > 100 && (
                <p className="text-sm text-muted-foreground mt-4 text-center">
                  Showing first 100 of {filteredGiftCards.length} gift cards
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Transactions */}
          {transactions && transactions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Latest gift card activity</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead>Date</TableHead>
                      <TableHead>Gift Card</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="text-right">Balance After</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.slice(0, 20).map((txn) => {
                      const giftCard = txn.gift_card as any;
                      return (
                        <TableRow key={txn.id}>
                          <TableCell className="text-muted-foreground">
                            {format(new Date(txn.created_at), 'MMM d, yyyy h:mm a')}
                          </TableCell>
                          <TableCell className="font-mono">
                            {giftCard?.code || '-'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={
                              txn.type === 'loaded' ? 'bg-green-100 text-green-800' :
                              txn.type === 'redeemed' ? 'bg-blue-100 text-blue-800' :
                              'bg-amber-100 text-amber-800'
                            }>
                              {txn.type}
                            </Badge>
                          </TableCell>
                          <TableCell className={`text-right tabular-nums font-medium ${
                            txn.type === 'loaded' ? 'text-green-600' : 'text-blue-600'
                          }`}>
                            {txn.type === 'loaded' ? '+' : '-'}{formatCurrency(Math.abs(txn.amount))}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCurrency(txn.balance_after)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Print template */}
      <div ref={printRef} className="print-source">
        <div className="print-header">
          <div>
            <h1>Gift Card Report</h1>
            <div className="print-meta">
              Status: {statusFilter === 'all' ? 'All' : statusFilter} • 
              Period: {DATE_RANGE_PRESETS.find(p => p.key === dateSel.preset)?.label
                ?? `${format(dateSel.start, 'MMM d, yyyy')} – ${format(dateSel.end, 'MMM d, yyyy')}`}
            </div>
          </div>
          <div className="print-meta">
            Generated on {new Date().toLocaleString()}
          </div>
        </div>

        <div className="print-summary">
          <div className="print-card">
            <div className="label">Total Cards</div>
            <div className="value">{summaryStats?.totalCards || 0}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Issued</div>
            <div className="value">{formatCurrency(summaryStats?.totalIssued || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Total Redeemed</div>
            <div className="value">{formatCurrency(summaryStats?.totalRedeemed || 0)}</div>
          </div>
          <div className="print-card">
            <div className="label">Outstanding</div>
            <div className="value">{formatCurrency(summaryStats?.outstandingBalance || 0)}</div>
          </div>
        </div>

        <div className="print-section">
          <h2>Gift Cards</h2>
          {filteredGiftCards.length > 0 ? (
            <table className="print-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Customer</th>
                  <th>Initial</th>
                  <th>Balance</th>
                  <th>Created</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {filteredGiftCards.map((card) => {
                  const customer = card.customer as any;
                  return (
                    <tr key={card.id}>
                      <td>{card.code}</td>
                      <td>{card.status}</td>
                      <td>{customer ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : '-'}</td>
                      <td>{formatCurrency(card.initial_balance)}</td>
                      <td>{formatCurrency(card.current_balance)}</td>
                      <td>{format(new Date(card.created_at), 'MMM d, yyyy')}</td>
                      <td>{card.expires_at ? format(new Date(card.expires_at), 'MMM d, yyyy') : 'Never'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="print-meta">No gift cards found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
